import React, { useCallback, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { Tab, Tabs } from "@mui/material";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "features/LayoutContainers/DashboardLayout";
import DashboardNavbar from "features/Navbars/DashboardNavbar";
import Footer from "features/Footer";
import ComplexStatisticsCard from "features/Cards/StatisticsCards";
import WithAuthWrapper from "withWrapper/withAuthWrapper";
import { useDispatch, useSelector } from "react-redux";
import RewardTable from "./components/rewardsTable/table";
import { getUserDetails } from "redux/slices/authenticationSlice";
import {
  getDashboard,
  getLoadingStatus,
  getDashboardSummaryThunk
} from "redux/slices/dashboardSlice";
import LoadingComponent from "components/LoadingComponent";
import ChartComponent from "./components/ChartComponent";
import PromotionBanner from "./components/CtaBanner";
import { getRewards, getRewardsThunk } from "redux/slices/voucherSlice";

const Dashboard: React.FC = () => {
  const accessToken = localStorage.getItem("stmpz-token") || "";
  const businessId = localStorage.getItem("stmpz-business-id") || "";
  const dispatch = useDispatch();
  const userDetails = useSelector(getUserDetails);
  const dashboardSummary = useSelector(getDashboard);
  const isLoading = useSelector(getLoadingStatus);
  const rewards = useSelector(getRewards);

  const [activeTab, setActiveTab] = useState<number>(0);
  const handleChange = (_, newValue) => {
    setActiveTab(newValue);
  };
  const getAllRewardTableList = useCallback(
    (page: number) => {
      dispatch(getRewardsThunk({ accessToken, businessId, page }));
    },
    [accessToken, businessId, dispatch]
  );
  useEffect(() => {
    if (
      accessToken &&
      !(Object.values(dashboardSummary).length > 0) &&
      Object.values(userDetails).length > 0
    ) {
      dispatch(
        getDashboardSummaryThunk({
          accessToken,
          businessId: userDetails.business_ids[0]
        })
      );
    }
  }, [accessToken, dashboardSummary, dispatch, userDetails, businessId]);

  useEffect(() => {
    getAllRewardTableList(1);
  }, [getAllRewardTableList]);

  const calculatePercentage = (value: number, total: number): number => {
    return Math.round((value / total) * 100);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {!!isLoading || !(Object.values(dashboardSummary).length > 0) ? (
        <LoadingComponent />
      ) : (
        <MDBox py={3}>
          <Tabs
            value={activeTab}
            onChange={handleChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Overall" value={0} />
            <Tab label="Rewards" value={1} />
            {/* <Tab label="Vouchers" value={3} /> */}
          </Tabs>

          {activeTab === 0 && (
            <MDBox mt={5}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      color={"primary"}
                      icon="person"
                      title="Total Users"
                      link={"/users"}
                      count={dashboardSummary.total_customers ?? "loading..."}
                      percentage={{
                        amount: calculatePercentage(100, 250),
                        label: `increase in last 30 days`,
                        color: "success"
                      }}
                    />
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      icon="leaderboard"
                      title="Total Stamps Given"
                      color={"secondary"}
                      count={
                        dashboardSummary.total_stamps_given ?? "loading..."
                      }
                      link={"/drivers"}
                      percentage={{
                        amount: dashboardSummary.stamps_given_growth_percentage,
                        label: `increase in last 30 days`,
                        color: "success"
                      }}
                      state={{ driverStatus: 0 }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      icon="redeem"
                      title="Total Rewards Given"
                      color={"info"}
                      count={
                        dashboardSummary.total_rewards_given ?? "loading..."
                      }
                      link={"/drivers"}
                      percentage={{
                        amount: dashboardSummary.rewards_growth_percentage,
                        label: `increase in last 30 days`,
                        color: "success"
                      }}
                      state={{ driverStatus: 0 }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      color={"success"}
                      icon="local_offer"
                      title="Total Vouchers Created"
                      count={10 ?? "loading..."}
                      percentage={{
                        amount: 10,
                        label: `increase in last 30 days`,
                        color: "info"
                      }}
                    />
                  </MDBox>
                </Grid>
              </Grid>
              <PromotionBanner
                suggestion={dashboardSummary.promotion_suggestion}
              />
              <MDBox mt={2}>
                {!!dashboardSummary.promotion_suggestion && (
                  <ChartComponent dashboardSummary={dashboardSummary} />
                )}
              </MDBox>
            </MDBox>
          )}

          {activeTab === 1 && (
            <MDBox mt={5}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      icon="leaderboard"
                      title="Total Rewards"
                      count={dashboardSummary.total_rewards_given ?? 0}
                      link={"/campaigns"}
                      state={{ campaignType: 0 }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      icon="leaderboard"
                      title="Card Saved"
                      count={dashboardSummary.cards_saved ?? 0}
                      link={"/campaigns"}
                      state={{ campaignType: 22 }}
                    />
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      icon="leaderboard"
                      title="Redeemed Rewards"
                      count={dashboardSummary.total_rewards_claimed ?? 0}
                      link={"/campaigns"}
                      state={{ campaignType: 20 }}
                    />
                  </MDBox>
                </Grid>
              </Grid>
              {rewards && Object.values(rewards).length > 0}
              {
                <RewardTable
                  header={[
                    { id: "sad Name", name: "Customer" },
                    { id: "status", name: "Reward" },
                    { id: "user", name: "Status" },
                    { id: "created_at", name: "User Claimed At" },
                    { id: "actions", name: "Created At" }
                  ]}
                  rows={rewards}
                  onPageChanged={(newPage: number) => {
                    getAllRewardTableList(newPage + 1);
                  }}
                  onReload={() => {}}
                />
              }
            </MDBox>
          )}
          {/* {activeTab === 2 && <MDBox mt={5}></MDBox>}
          {activeTab === 3 && (
            <MDBox mt={5}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={4}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      icon="leaderboard"
                      title="Total Vouchers"
                      count={` ${20 ?? 0}`}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      icon="leaderboard"
                      title="Total Earnt"
                      count={` $${2000 ?? 0}`}
                    />
                  </MDBox>
                </Grid>
              </Grid>
              <VoucherTable
                header={[
                  { id: "voucher Name", name: "Voucher Name" },
                  { id: "Price", name: "Price" },
                  { id: "user", name: "Status" },
                  { id: "created_at", name: "Current Purchase" },
                  { id: "type", name: "Type" },
                  { id: "actions", name: "Created At" }
                ]}
                rows={mockVouchers}
                onPageChanged={(newPage: number) => {}}
                onReload={() => {}}
              />
            </MDBox>
          )} */}
        </MDBox>
      )}
      <Footer />
    </DashboardLayout>
  );
};

export default WithAuthWrapper(Dashboard);
